<script lang="ts">

    import 'swiper/css/bundle';
    import { afterUpdate, createEventDispatcher, onMount, setContext } from 'svelte';
    import { Swiper, Lazy, Pagination } from 'swiper';
    import type { LazyOptions, PaginationOptions } from 'swiper/types';

    Swiper.use([Lazy, Pagination]);

    export let slideTo: any = null;
    export let autoHeight: boolean = false;
    export let preloadImages: boolean = true;
    export let lazy: boolean | LazyOptions = false;
    export let activeIndex: number = undefined;
    export let pagination: boolean | PaginationOptions = false;
    export let slidesPerView: number | 'auto' = 1;
    export let slidesPerGroup: number = 1;

    const slidesMap = new Map<any, HTMLElement>();
    const dispatch = createEventDispatcher();

    let swiperContainer: HTMLElement;
    let swiper: Swiper;

    $: slideToIndex(slideTo);

    setContext('registerSlide', (slideIndex: any, slideElement: HTMLElement) => {
        if (!slideIndex || slidesMap.has(slideIndex)) {
            return;
        }
        slidesMap.set(slideIndex, slideElement);
        if (slideIndex === slideTo) {
            slideToIndex(slideIndex);
        }
        swiper?.update();
    });

    setContext('unregisterSlide', (index: any) => {
        slidesMap.delete(index);
    });

    onMount(() => {

        const paginationDefaults: PaginationOptions = { el: '.swiper-pagination' };
        let paginationOptions: boolean | PaginationOptions = false;
        
        if (pagination === true) {
            paginationOptions = paginationDefaults;
        } else if (pagination) {
            paginationOptions = { ...pagination, ...paginationDefaults };
        }

        swiper = new Swiper(swiperContainer, {
            autoHeight,
            preloadImages,
            lazy,
            pagination: paginationOptions,
            slidesPerView,
            slidesPerGroup,
            on: {
                afterInit: swiper => activeIndex = swiper.activeIndex,
                slideChange: onSlideChange,
                slideChangeTransitionEnd: () => swiper.updateAutoHeight(),
            }
        });

        if (slideTo) {
            slideToIndex(slideTo);
        }

    });

    afterUpdate(() => {
        update();
    });

    export function update() {
        swiper?.update();
    }

    export function next() {
        swiper?.slideNext();
    }

    export function prev() {
        swiper?.slidePrev();
    }

    function onSlideChange() {
        dispatch('change', swiper.activeIndex);
    }

    function slideToIndex(index: any) {
        if (!swiper || !index) {
            return;
        }
        const slideElement = slidesMap.get(index);
        const slideIndex = swiper.slides.indexOf(slideElement);
        swiper.slideTo(slideIndex, 0);
        activeIndex = slideIndex;
        dispatch('change', index);
    }

</script>

<style lang="scss">

    .swiper-container {
        flex: 1 1 0;
        height: var(--slider-height, auto);
        min-height: var(--slider-min-height, auto);
        width: 100%;
        touch-action: auto;
        pointer-events: auto;
        overflow: hidden;
    }

</style>

<div class="swiper-container" bind:this={swiperContainer}>
    <div class="swiper-wrapper">
        <slot></slot>
    </div>
    <div class="swiper-pagination"></div>
</div>
